import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { TieredMenu } from 'primereact/tieredmenu';
import { classNames } from 'primereact/utils';
import { confirmDialog } from 'primereact/confirmdialog';
import moment from 'moment';

import PriorityDropdown from 'components/UI/PriorityDropdown/PriorityDropdown';
import ImageComponent from 'shared/ImageComponent';
import { generateFullName } from 'utils/generateFullNameUtils';
import { dollarFormat, getTenantDetails } from 'utils/utils';
import { useDraggableCard } from '../hooks/useDraggableCard';
import { useDroppableCard } from '../hooks/useDroppableCard';
import { useBoardActions, useBoardData, useIsEntityLocked } from '../context/BoardContext';
import { useLeadDescription } from '../hooks/useLeadDescription';
import { useLeadActions } from '../context/LeadActionsContext';
import { closeLead } from 'services/lead/leadServices';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { PI_PROVIDERS_TYPE } from 'constants';

const noOfDaysInColumn = (stageSK, timeLine) => {
  return moment().diff(timeLine?.find((v) => v.lead_stage === stageSK)?.started_at, 'days');
};

const hasDraftNameData = (data) => Boolean(data?.first_name || data?.last_name || data?.title);

const getDraftContact = (draftPayload) => {
  const candidates = [draftPayload?.main_client, draftPayload?.client, draftPayload?.contact];
  return candidates.find(hasDraftNameData);
};

const getCardLeadTitle = (item) => {
  if (item?.is_draft) {
    return generateFullName(getDraftContact(item?.draft_payload)) || 'No Name';
  } else {
    let leadTitle = '';
    let contactType = item?.main_client?.contact_type;
    if (contactType === 'business' || PI_PROVIDERS_TYPE.includes(contactType)) {
      leadTitle = item?.main_client?.title;
    } else if (contactType === 'person') {
      leadTitle = generateFullName(item?.main_client);
    }
    return leadTitle;
  }
};

function LeadCard({ item, columnId, columnStageName, index, onDrop, setCardHeight }) {
  // Get lock state from context - only rerenders if THIS card's lock state changes
  const isLocked = useIsEntityLocked(`task-${item.sk}`);
  const { leadStages } = useBoardData();
  const { updateCard, moveCardToStage, resetBoard } = useBoardActions();
  const { openConvertCase, openCloseLead } = useLeadActions();
  const { addToast } = useToast();
  const [tenantId] = getTenantDetails();

  const { showDescription, leadDescription, isLoadingDescription, toggleDescription } = useLeadDescription(item);

  const menu = useRef(null);
  const cardContentRef = useRef(null);

  const handleConvertToCase = () => {
    // Open the Convert to Case dialog with the full lead item
    openConvertCase(item);
  };

  const handleCloseLead = () => {
    // Open the Close Lead dialog with the full lead item
    openCloseLead(item);
  };

  const handleReopenLead = () => {
    closeLead(item.sk, { is_open: true })
      .then(() => {
        // Refresh the board to reflect the reopened lead
        resetBoard();
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.LEAD_OPEN_SUCCESS);
      })
      .catch(() => {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.LEAD_OPEN_FAILURE);
      });
  };

  const confirmReOpen = () => {
    confirmDialog({
      className: 'common-popup-style',
      icon: 'pi pi-exclamation-triangle',
      header: 'Confirm Reopen Lead',
      message: 'Do you want to reopen this lead?',
      rejectClassName: 'outline',
      accept: handleReopenLead,
      onHide: () => {},
      reject: () => {},
    });
  };

  const handleMoveToStage = (targetStageId) => {
    // Check if moving to Closed stage
    if (isClosedStage(targetStageId) && item.is_open) {
      // Open close lead dialog instead of direct move
      openCloseLead(item);
      return;
    }

    // Move card to the selected stage for other stages
    moveCardToStage(item.sk, columnId, targetStageId);
  };

  // Helper to check if a stage is the Closed stage
  const isClosedStage = useCallback(
    (stageId) => {
      const stage = leadStages?.find((s) => s.sk === stageId);
      return stage?.lead_stage_name === 'Closed';
    },
    [leadStages]
  );

  // Intercept drags to Closed stage and open dialog instead
  const handleDrop = useCallback(
    (dropData) => {
      const { targetColumnId, leadItem: draggedLeadItem } = dropData;

      // Use the leadItem from drag data if available, otherwise use current item
      const leadToCheck = draggedLeadItem || item;

      // If dragging to Closed stage from a different stage, open dialog instead
      if (isClosedStage(targetColumnId) && columnId !== targetColumnId && leadToCheck.is_open) {
        openCloseLead(leadToCheck);
        return; // Intercept - don't call onDrop
      }

      // Normal drag behavior for other stages
      onDrop(dropData);
    },
    [columnId, isClosedStage, item, openCloseLead, onDrop]
  );

  const menuItems = useMemo(
    () => [
      {
        label: 'Convert to Case',
        show: item?.is_open,
        command: handleConvertToCase,
      },
      {
        label: 'Close Lead',
        show: item?.is_open,
        command: handleCloseLead,
      },
      {
        label: 'Reopen Lead',
        show: !item?.is_open,
        command: confirmReOpen,
      },
      {
        label: 'Move To',
        show: item?.is_open,
        command: () => {},
        items: leadStages
          ?.filter((stage) => stage.sk !== item.lead_stage)
          ?.map((stage) => ({
            label: stage.lead_stage_name,
            command: () => handleMoveToStage(stage.sk),
          })),
      },
    ],
    [item?.is_open, item.lead_stage, leadStages, handleConvertToCase, handleCloseLead, confirmReOpen, handleMoveToStage]
  );

  const filteredMenuItems = useMemo(() => menuItems.filter((v) => v.show), [menuItems]);

  // Memoize derived values to prevent unnecessary calculations
  const leadAssigned = useMemo(() => item?.draft_payload?.lead_assigned_to || item?.assign_to || []);

  const cardLeadTitle = useMemo(() => getCardLeadTitle(item), [item]);

  const daysInColumn = useMemo(() => noOfDaysInColumn(item?.lead_stage, item?.lead_timeline), [item?.lead_stage, item?.lead_timeline]);

  const practiceArea = useMemo(
    () =>
      item?.practice_area_name ||
      item?.draft_payload?.case_description?._practice_area_ ||
      item?.draft_payload?._practice_area_ ||
      item?.draft_payload?.case_description?.case_practice_area ||
      '-',
    [item?.practice_area_name, item?.draft_payload]
  );

  const potentialCaseValue = useMemo(
    () => item?.potential_case_value || item?.draft_payload?.case_description?.potential_case_value,
    [item?.potential_case_value, item?.draft_payload?.case_description?.potential_case_value]
  );

  const isDragDisabled = useMemo(() => {
    const isDraftLead = item?.is_draft === true;
    const isClosedLead = columnStageName?.toLowerCase() === 'closed';
    return isLocked || isDraftLead || isClosedLead;
  }, [isLocked, item?.is_draft, columnStageName]);

  const { elementRef, isDragging } = useDraggableCard({
    cardId: item.sk,
    columnId,
    index,
    canDrag: !isDragDisabled, // Disable drag if locked, draft, or closed
    leadItem: item, // Pass full lead item for interception
  });

  const { isOver, edge } = useDroppableCard({
    elementRef,
    cardId: item.sk,
    columnId,
    onDrop: handleDrop,
  });

  // Determine title text based on state
  const cardTitle = useMemo(() => {
    if (isLocked) return '⏳ Syncing... Please wait';
    if (isDragDisabled) return 'Cannot drag draft or closed leads';
    return '';
  }, [isLocked, isDragDisabled]);

  useEffect(() => {
    const measureHeight = () => {
      if (cardContentRef?.current?.clientHeight) {
        setCardHeight(item.sk, cardContentRef.current.clientHeight);
      }
    };
    const rafId = requestAnimationFrame(() => {
      measureHeight();
    });
    return () => cancelAnimationFrame(rafId);
  }, [item.sk, setCardHeight, showDescription, leadDescription, isLoadingDescription]); // Re-measure when description toggles or loads

  return (
    <div className="item-wrapper" ref={cardContentRef} style={{ marginBottom: '0' }}>
      <div
        ref={elementRef}
        className={`item ${isDragging ? 'is-dragging' : ''} ${isLocked ? 'syncing' : ''} ${
          isDragDisabled && !isLocked ? 'drag-disabled' : ''
        }`}
        style={{
          position: 'relative',
          // opacity: isDragging ? 0.9 : 1,
          opacity: isDragging ? 0.9 : isLocked ? 0.7 : 1,
          cursor: isLocked ? 'not-allowed' : isDragDisabled ? 'default' : 'grab',
          pointerEvents: isLocked ? 'none' : 'auto',
          transition: 'all 0.2s ease',
        }}
        title={cardTitle}
      >
        {/* Syncing Indicator Baxdge */}
        {isLocked && (
          <div
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              backgroundColor: '#0052CC',
              borderRadius: '12px',
              padding: '4px 8px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              zIndex: 101,
              boxShadow: '0 2px 8px rgba(0, 82, 204, 0.3)',
            }}
          >
            <i
              className="pi pi-spin pi-spinner"
              style={{
                fontSize: '10px',
                color: '#FFFFFF',
              }}
            />
            <span
              style={{
                color: '#FFFFFF',
                fontSize: '11px',
                fontWeight: 500,
                letterSpacing: '0.3px',
              }}
            >
              Syncing
            </span>
          </div>
        )}

        {/* Drop indicators */}
        {edge === 'top' && isOver && (
          <div
            style={{
              position: 'absolute',
              top: '-6px',
              left: '8px',
              right: '8px',
              height: '4px',
              background: 'linear-gradient(90deg, #0052CC 0%, #0066FF 100%)',
              borderRadius: '3px',
              zIndex: 1001,
              boxShadow: '0 0 12px rgba(0, 82, 204, 0.9), 0 0 4px rgba(0, 82, 204, 1)',
              animation: 'pulse-card-indicator 1s ease-in-out infinite',
              pointerEvents: 'none',
            }}
          />
        )}
        {edge === 'bottom' && isOver && (
          <div
            style={{
              position: 'absolute',
              bottom: '-6px',
              left: '8px',
              right: '8px',
              height: '4px',
              background: 'linear-gradient(90deg, #0052CC 0%, #0066FF 100%)',
              borderRadius: '3px',
              zIndex: 1001,
              boxShadow: '0 0 12px rgba(0, 82, 204, 0.9), 0 0 4px rgba(0, 82, 204, 1)',
              animation: 'pulse-card-indicator 1s ease-in-out infinite',
              pointerEvents: 'none',
            }}
          />
        )}
        <div className="container w-100 card-layout card-bg p-3">
          <div className="d-flex align-items-center pb-2">
            <div className="col-8">
              <Link
                className="task-title mb-0 text-bold redirection-link-no-text-transform"
                style={{ width: 'fit-content' }}
                to={item?.is_draft ? `/leads/new/${item?.sk?.split('#')?.pop()}` : `/leads/${item?.sk?.split('#')?.pop()}`}
              >
                {cardLeadTitle}
              </Link>
            </div>
            <div className="col-4 d-flex justify-content-end">
              {item?.is_draft ? (
                <span className="tag m-1 ms-0 draft-tag">Draft</span>
              ) : (
                <>
                  <PriorityDropdown
                    className="w-100 border rounded priority-dropdown align-items-center me-2"
                    value={item?.lead_details?.priority}
                    onChange={(e) => {
                      updateCard(item?.sk, columnId, { lead_details: { ...item?.lead_details, priority: e.value } });
                    }}
                    iconOnly
                    filter
                    placeholder=""
                    disabled={Boolean(item?.is_draft)}
                  />
                  <div
                    className="d-flex border border-1 rounded  justify-content-center p-2 task-lane-header-options pointer"
                    onClick={(e) => menu.current.toggle(e)}
                  >
                    <i className="pi pi-ellipsis-v d-flex align-items-center"></i>
                    <TieredMenu model={filteredMenuItems} popup ref={menu} id="popup_menu" />
                  </div>
                </>
              )}
            </div>
          </div>
          <hr className="my-1"></hr>
          <div className="d-flex align-items-center justify-content-between py-2">
            <Link
              to={leadAssigned?.firm_user_id ? `/settings/team-management/${leadAssigned?.firm_user_id}` : '/settings/team-management'}
              className={`text-decoration-none ${leadAssigned?.firm_user_id ? '' : 'pe-none'}`}
            >
              <div className="col-12 d-flex align-items-center">
                <ImageComponent
                  filePath={`${tenantId}/${leadAssigned?.firm_user_id}/profile_image`}
                  fileName={generateFullName(leadAssigned) || 'C'}
                  fileSize="medium.jpg"
                  avatarSize="medium"
                  className={classNames({ 'pe-none': !leadAssigned?.firm_user_id })}
                />

                <div className={classNames({ 'redirection-link-no-text-transform': true, 'ms-1': !leadAssigned?.first_name })}>
                  {leadAssigned?.first_name && <i className="fas fa-user-tie me-2"></i>}
                  {leadAssigned?.first_name ? generateFullName(leadAssigned) : 'Unassigned'}{' '}
                </div>
              </div>
            </Link>
          </div>
          <div className="d-flex align-items-center justify-content-between py-1">
            <div className="col-7 d-flex align-items-center black-600 F-size14">
              <i className="icon-workload pe-1" />
              Practice Area
            </div>
            <div className="col-5">
              <div className="taskcreate-date d-flex justify-content-end">
                <span className="text-truncate d-inline-block w-100 text-end">{practiceArea}</span>
              </div>
            </div>
          </div>
          {potentialCaseValue && (
            <div className="d-flex align-items-center justify-content-between py-1">
              <div className="col-8 d-flex align-items-center black-600 F-size14">
                <i className="icon-money pe-1" />
                Potential Case Value
              </div>
              <div className="col-4">
                <div className="taskcreate-date d-flex justify-content-end">
                  <span className="text-truncate d-inline-block w-100 text-end">{dollarFormat(potentialCaseValue)}</span>
                </div>
              </div>
            </div>
          )}
          <div className="date-wrap d-flex align-items-center py-1">
            <div className="col-8 d-flex align-items-center black-600 F-size14">
              <i className="icon-clock pe-1" /> In this Column
            </div>
            <div className="col-4 d-flex justify-content-end">{daysInColumn}d</div>
          </div>

          <div className="d-flex py-2">
            <div className="col-12 d-flex justify-content-between">
              <div className="small pointer d-flex align-items-center" onClick={toggleDescription}>
                View Description
                {showDescription ? <i className="pi pi-angle-up ms-2" /> : <i className="pi pi-angle-down ms-2" />}
              </div>
            </div>
          </div>

          {showDescription && (
            <div className="d-flex flex-column">
              <div className="col-12 d-flex justify-content-between border p-2 my-1" style={{ borderRadius: '8px' }}>
                <div className="d-flex align-items-start F-size13 lead-description-scroll">
                  {isLoadingDescription ? (
                    <div>
                      <i className="pi pi-spin pi-spinner me-2"></i>
                      <span className="text-dark">Loading description...</span>
                    </div>
                  ) : (
                    <div style={{ width: '100%', height: 'auto', whiteSpace: 'pre-wrap' }} className="text-break">
                      {!leadDescription?.content || leadDescription?.content === ''
                        ? 'No description available'
                        : leadDescription?.content || 'No description available'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Memoize with custom comparison function - only rerender if essential props change
// Return TRUE if props are equal (prevent rerender)
export default React.memo(LeadCard, (prevProps, nextProps) => {
  return (
    prevProps.item.sk === nextProps.item.sk &&
    prevProps.columnId === nextProps.columnId &&
    prevProps.index === nextProps.index &&
    // Compare relevant item properties that affect rendering
    prevProps.item.lead_stage === nextProps.item.lead_stage &&
    prevProps.item.is_draft === nextProps.item.is_draft &&
    prevProps.item.is_open === nextProps.item.is_open &&
    prevProps.item.lead_details?.priority === nextProps.item.lead_details?.priority
  );
});
